import { cn, cva, NoddiIcon } from 'noddi-ui-common';
import { NoddiButton, NoddiSelect, NoddiSkeleton } from 'noddi-ui-web';
import { Fragment, useEffect, useRef } from 'react';
import { useShowSlotAvailability } from '../../stores/DevSettingsStore';
import { hasSlotCapacityToShow } from '../Capacity/slotCapacityInfo';
import { SlotCapacityInline } from '../Capacity/SlotCapacityInline';
import { SlotCapacityTooltip } from '../Capacity/SlotCapacityTooltip';
import { DayWithSlots, StationaryTimeSlotPickerTranslations, TimeSlot } from './types';

const slotChipVariants = cva(
  'flex w-full flex-col items-center rounded-lg border px-3 py-2 text-sm transition-colors',
  {
    variants: {
      // Same brand tokens as NoddiSelectableTile -- `--primary` is a near-black in most
      // brands and `violet-50` was Noddi's hue hardcoded, so both ignored the brand.
      selected: {
        true: 'border-card-selected-background bg-card-selected-background text-card-selected-foreground hover:bg-card-selected-background',
        false: 'border-border bg-card hover:border-card-selected-background hover:bg-card-light-background'
      },
      showCapacity: {
        true: 'min-h-20 gap-0.5',
        false: 'gap-0.5'
      }
    },
    defaultVariants: {
      selected: false,
      showCapacity: false
    }
  }
);

const slotMetaVariants = cva('text-xs', {
  variants: {
    selected: {
      true: 'opacity-80',
      false: 'text-muted-foreground'
    }
  }
});

type Props = {
  days: DayWithSlots[];
  selectedSlotId: string | null;
  onSelectSlot: (slot: TimeSlot) => void;
  onNavigatePrevious: () => void;
  onNavigateNext: () => void;
  isPreviousDisabled?: boolean;
  isNextDisabled?: boolean;
  dateRangeLabel: string;
  isPending?: boolean;
  /** Tile currently being held — shows a spinner in place of its label. */
  pendingSlotId?: string | null;
  showAvailableCount?: boolean;
  // Cap the slot list with internal scrolling so surrounding chrome (e.g. a
  // dialog's nav buttons) stays visible. Pages should let the list flow full height.
  constrainListHeight?: boolean;
  /**
   * Bays the booking can be pinned to, with display-ready names. Pass together with
   * `onSelectWorkstation` to expose the picker; omit for auto-assign-only surfaces
   * (customer flows), which must never see internal bay names.
   */
  workstations?: { id: number; name: string }[];
  /** `undefined` = auto-assign. */
  selectedWorkstationId?: number;
  onSelectWorkstation?: (workstationId: number | undefined) => void;
  /**
   * `yyyyMMdd` of the day the list should open on. Scrolled into view once, when
   * that day is in the current week.
   */
  focusDateKey?: string;
  translations?: StationaryTimeSlotPickerTranslations;
};

export const StationaryTimeSlotPicker = ({
  days,
  selectedSlotId,
  onSelectSlot,
  onNavigatePrevious,
  onNavigateNext,
  isPreviousDisabled,
  isNextDisabled,
  dateRangeLabel,
  isPending,
  pendingSlotId,
  showAvailableCount,
  constrainListHeight,
  workstations,
  selectedWorkstationId,
  onSelectWorkstation,
  focusDateKey,
  translations
}: Props) => {
  const showSlotAvailability = useShowSlotAvailability();
  const focusRef = useRef<HTMLDivElement | null>(null);
  const didFocus = useRef(false);

  useEffect(() => {
    if (didFocus.current || isPending || !focusDateKey || !focusRef.current) {
      return;
    }
    didFocus.current = true;
    // `nearest` leaves a day that is already on screen where it is.
    focusRef.current.scrollIntoView({ block: 'nearest' });
  }, [focusDateKey, isPending, days]);
  const previousLabel = translations?.previous ?? 'Previous';
  const nextLabel = translations?.next ?? 'Next';
  const autoAssignLabel = translations?.workstationAutoAssign ?? 'Auto-assign';
  // One bay leaves nothing to choose, so the control would only add noise.
  const showWorkstationPicker = onSelectWorkstation != null && (workstations?.length ?? 0) > 1;

  // Pinning to one bay empties most periods, so name it — otherwise the picker
  // reads as "the garage is full" when only that bay is.
  const pinnedWorkstation = workstations?.find((workstation) => workstation.id === selectedWorkstationId);
  const emptyStateLabel = pinnedWorkstation
    ? `${pinnedWorkstation.name} has no available time slots in this period`
    : (translations?.emptyState ?? 'No available time slots in this period');

  return (
    <div className='flex flex-col gap-4'>
      {showWorkstationPicker && (
        <NoddiSelect<number>
          label={translations?.workstationLabel ?? 'Workstation'}
          tooltip={
            translations?.workstationTooltip ??
            'Leave on auto-assign to let any available workstation take the booking, or pick one to show only its free times and pin the booking to it.'
          }
          startIcon='Wrench'
          placeholder={autoAssignLabel}
          options={workstations?.map((workstation) => ({ label: workstation.name, value: workstation.id }))}
          value={selectedWorkstationId}
          onChange={onSelectWorkstation}
          searchable={(workstations?.length ?? 0) > 8}
          className='sm:max-w-xs'
        />
      )}

      <div className='flex items-center justify-between'>
        <NoddiButton
          startIcon='ArrowLeft'
          variant='outline'
          size='small'
          onClick={onNavigatePrevious}
          disabled={isPreviousDisabled}
        >
          {previousLabel}
        </NoddiButton>
        {/* Phones keep the buttons alone on the row; the range is still announced when it pages. */}
        <span className='sr-only text-sm font-medium md:not-sr-only' aria-live='polite' aria-atomic='true'>
          {dateRangeLabel}
        </span>
        <NoddiButton
          endIcon='ArrowRight'
          variant='outline'
          size='small'
          onClick={onNavigateNext}
          disabled={isNextDisabled}
        >
          {nextLabel}
        </NoddiButton>
      </div>

      {isPending ? (
        <div className='flex flex-col gap-4' role='status'>
          <span className='sr-only'>{translations?.loading ?? 'Loading'}</span>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className='flex flex-col gap-2'>
              <NoddiSkeleton className='h-5 w-32' />
              <div className='grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6'>
                {Array.from({ length: 8 }).map((_, j) => (
                  <NoddiSkeleton key={j} className='h-12 w-full rounded-lg' />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : days.length === 0 ? (
        <div className='flex flex-col items-center gap-4 py-6 text-gray-600' role='status'>
          <NoddiIcon name='CalendarX' size='medium' />
          <span className='text-sm'>{emptyStateLabel}</span>
        </div>
      ) : (
        <div className={cn('flex flex-col gap-4', constrainListHeight && '-m-1 max-h-[50vh] overflow-auto p-1')}>
          {days.map((day) => (
            <div
              key={day.dateLabel}
              ref={day.dateKey === focusDateKey ? focusRef : undefined}
              className='flex flex-col gap-2'
            >
              <h2 className='text-sm font-semibold'>{day.dateLabel}</h2>
              <div className='grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6'>
                {day.slots.map((slot) => {
                  const isSelected = selectedSlotId === slot.id;
                  const isSlotPending = pendingSlotId === slot.id;
                  const showCapacity = showSlotAvailability && hasSlotCapacityToShow(slot.capacity);

                  const button = (
                    <button
                      type='button'
                      aria-pressed={isSelected}
                      aria-busy={isSlotPending}
                      aria-label={[day.dateLabel, slot.label, slot.subLabel].filter(Boolean).join(', ')}
                      // A second click during the hold would reserve twice and orphan the first bay.
                      disabled={pendingSlotId != null}
                      onClick={() => onSelectSlot(slot)}
                      className={cn(
                        slotChipVariants({ selected: isSelected, showCapacity }),
                        'relative',
                        pendingSlotId != null && !isSlotPending && 'opacity-50'
                      )}
                    >
                      {isSlotPending && (
                        <span className='absolute inset-0 flex items-center justify-center'>
                          {/* Spin a wrapper, never the icon: lucide forwards className onto every
                              child shape, so animate-spin rotates each arc about its own origin. */}
                          <span className='flex animate-spin'>
                            <NoddiIcon name='Loader2' size='small' />
                          </span>
                        </span>
                      )}
                      {/* Hidden, not unmounted: a tile is only as wide as a 6-column grid cell, so
                          swapping its content for a spinner resizes the tile and reflows the grid. */}
                      <span className={cn('flex w-full flex-col items-center gap-0.5', isSlotPending && 'invisible')}>
                        <span className='font-medium'>{slot.label}</span>
                        {showAvailableCount && slot.availableCount != null && (
                          <span className={slotMetaVariants({ selected: isSelected })}>
                            {slot.availableCount}
                            {slot.totalWorkstations != null && `/${slot.totalWorkstations}`}
                          </span>
                        )}
                        {slot.subLabel && (
                          <span className={slotMetaVariants({ selected: isSelected })}>{slot.subLabel}</span>
                        )}
                        {showCapacity && slot.capacity && (
                          <SlotCapacityInline
                            capacity={slot.capacity}
                            isSelected={isSelected}
                            className={isSelected ? 'opacity-80' : undefined}
                          />
                        )}
                      </span>
                    </button>
                  );

                  return (
                    <Fragment key={slot.id}>
                      {showCapacity && slot.capacity ? (
                        <SlotCapacityTooltip capacity={slot.capacity} enabled>
                          {button}
                        </SlotCapacityTooltip>
                      ) : (
                        button
                      )}
                    </Fragment>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
